import React from "react";
import type { FAQ } from "@/data/faqs";
import { FAQPageSchema } from "@/components/seo/StructuredData";

/**
 * FaqSection — a disclosure-based FAQ block with schema.org FAQPage JSON-LD.
 *
 * Uses native <details>/<summary> so it works without JavaScript and is
 * fully keyboard-accessible by default. JSON-LD is emitted so Google and
 * AI search surfaces can lift the Q&A directly.
 */
export function FaqSection({
  faqs,
  heading = "Frequently Asked Questions",
  emitSchema = true,
  id = "faq",
}: {
  faqs: FAQ[];
  heading?: string;
  emitSchema?: boolean;
  id?: string;
}) {
  return (
    <section id={id} aria-labelledby={`${id}-heading`} className="w-full">
      {emitSchema && <FAQPageSchema faqs={faqs} />}
      <h2
        id={`${id}-heading`}
        className="text-2xl md:text-3xl font-serif text-ink mb-6"
      >
        {heading}
      </h2>
      <dl className="border-t border-hairline">
        {faqs.map((faq) => (
          <details
            key={faq.id}
            className="group border-b border-hairline py-5"
          >
            <summary className="list-none cursor-pointer flex items-start justify-between gap-6">
              <dt className="font-serif text-lg md:text-xl text-ink leading-snug">
                {faq.question}
              </dt>
              <span
                aria-hidden="true"
                className="font-mono text-sm text-ink-3 mt-1 shrink-0 transition-transform group-open:rotate-45"
              >
                +
              </span>
            </summary>
            <dd className="mt-4 text-ink-2 font-serif leading-relaxed max-w-3xl">
              {faq.answer}
            </dd>
          </details>
        ))}
      </dl>
    </section>
  );
}
